import {onFind, initInScope, cleanUpInScope} from "../init-modules-in-scope";
import {find, findAllIn, hasClass, closest, findIn, trigger, triggerWith, on, removeAttribute, setAttribute, addClass, removeClass} from '../dom-utils';
import {getPrefixedDataSet} from '../data-set-utils';
import fetch from "../fetch";
import escapeHtml from "escape-html";

const defaultSelectors = {
    base: '.js-product-grid',
    content: '.js-product-grid__content',
    container: '.js-product-grid__container',
};

export function init(options = {}, selectors = defaultSelectors) {
    createProductGrid(
        find(selectors.base),
        {...options},
        {...defaultSelectors, ...selectors}
    );
}

export function createProductGrid(productGridElement, options = {}, selectors = defaultSelectors) {
    options = {
        ...defaultSelectors,
        ...options
    };

    selectors = {
        ...defaultSelectors,
        ...selectors
    };

    if (!productGridElement) {
        return;
    }

    let newState = Object.assign({}, history.state, {
        productGridAjaxUrl: productGridElement.getAttribute('data-product-grid-ajax-url'),
        productGridInputState: getParams(productGridElement),
        productGridAdditionalInputState: getAdditionalInputState(productGridElement)
    });

    if (options.disableScrollRestoration && 'scrollRestoration' in history) {
        // in this case the location on the page is not restored when the user clicks the back button
        // this is useful if the load-more-button (js-product-grid__load-more-link) variant of the product grid is used
        history.scrollRestoration = 'manual';
    }

    history.replaceState(newState, document.title);


    window.addEventListener('popstate', (evt) => {
        if (evt.state.productGridAjaxUrl) {
            setInputState(productGridElement, evt.state.productGridInputState.filter(function (currentInput) {
                return currentInput.value !== '$$EMPTY$$'
            }));

            productGridElement.getAttribute('data-additional-input-state', evt.state.productGridAdditionalInputState);

            loadUrl(
                productGridElement,
                evt.state.productGridAjaxUrl,
                evt.state.productGridInputState
            ).catch((error, requestState) => {
                /*reset the input state; otherwise the url params and the form inputs would not be the same*/
                setInputState(productGridElement, evt.state.productGridInputState);
                console.error(error, requestState);
            });
        }
    });

    onFind('.js-product-grid__link', function (item) {
        on('click', function (evt) {
            let productGridElement = closest('.js-product-grid', item);
            let url = item.getAttribute('href');
            let ajaxUrl = item.getAttribute('data-product-grid-ajax-url') || url;
            evt.preventDefault();

            productGridElement.getAttribute('data-additional-input-state', []);

            loadUrl(productGridElement, ajaxUrl).then(function () {
                triggerWith('product-grid.changed', [getInputState(productGridElement)], productGridElement);
                history.replaceState({
                    'productGridAjaxUrl': ajaxUrl,
                    'productGridInputState': getParams(productGridElement),
                    'productGridAdditionalInputState': []
                }, document.title);
            });

            /* the state is replace with the real form state after the grid is done loading the content.
            * This is needed because we can't know the input state before the form is loaded*/
            history.pushState({
                    'productGridAjaxUrl': ajaxUrl,
                    'productGridInputState': [],
                    'productGridAdditionalInputState': []
                },
                document.title,
                url
            );
        }, item);
    });

    onFind(selectors.content, function (productGridContent) {
        productGridElement = closest(selectors.base, productGridContent);

        if (!productGridElement) {
            return;
        }

        let changedInputs = getChangeInputElements(productGridElement);
        changedInputs.map((input) => {
            const setInputs = () => {
                reloadGrid(closest('.js-product-grid', input));
                // change not submitted sections immediately
                setInputState(productGridElement, getInputState(productGridElement));
            };
            input.addEventListener('change', setInputs);
            return {
                cleanUp: () => {
                    input.removeEventListener('change', setInputs);
                }
            };
        });

        let submitButtons = findAllIn('.js-product-grid__submit-section-btn', productGridElement);
        submitButtons.map((button) => {
            const submit = (evt) => {
                evt.preventDefault();
                let productGridElement = closest('.js-product-grid', button);
                updateSubmitSectionDataValues(closest('.js-product-grid__submit-section', button));
                reloadGrid(productGridElement);
            };

            button.addEventListener('click', submit);

            return {
                cleanUp: () => {
                    button.removeEventListener('click', submit);
                }
            };

            // on('click', function (evt) {
            //     evt.preventDefault();
            //     let productGridElement = closest('.js-product-grid', button);
            //     console.log(button)
            //     updateSubmitSectionDataValues(closest('.js-product-grid__submit-section', button));
            //     reloadGrid(productGridElement);
            // }, button);
        });

        let submitSections = findAllIn('.js-product-grid__submit-section', productGridElement);
        submitSections.map((section) => {
            updateSubmitSectionDataValues(section);

            on('keydown', function (evt) {
                if (evt.keyCode === 13) {
                    let productGridElement = closest('.js-product-grid', section);
                    evt.preventDefault();
                    updateSubmitSectionDataValues(section);

                    reloadGrid(productGridElement);
                }
            }, section);
        });

        let contentLinks = findAllIn('.js-product-grid__content-link', productGridElement);
        contentLinks.map((link) => {
            on('click', function (evt) {
                let productGridElement = closest('.js-product-grid', link);
                let url = link.getAttribute('href');
                let ajaxUrl = link.getAttribute('data-product-grid-ajax-url') || url;
                evt.preventDefault();

                loadUrl(productGridElement, ajaxUrl, [], true);
                history.pushState({
                        'productGridAjaxUrl': ajaxUrl,
                        'productGridInputState': getParams(productGridElement),
                        'productGridAdditionalInputState': getAdditionalInputState(productGridElement)
                    },
                    document.title,
                    url
                );
            }, link);
        });

        let loadMoreLinks = findAllIn('.js-product-grid__load-more-link', productGridElement);
        loadMoreLinks.map((link) => {
            on('click', function (evt) {
                let productGridElement = closest('.js-product-grid', link);

                let url = link.getAttribute('href');
                let ajaxUrl = link.getAttribute('data-product-grid-ajax-url') || url;
                evt.preventDefault();

                loadUrl(productGridElement, ajaxUrl, [], true, link.getAttribute('data-load-more-type'));
                history.replaceState({
                        'productGridAjaxUrl': ajaxUrl,
                        'productGridInputState': getParams(productGridElement),
                        'productGridAdditionalInputState': getAdditionalInputState(productGridElement)
                    },
                    document.title,
                    url
                );
            }, link);
        });

        let removeInputButtons = findAllIn('.js-product-grid__remove-input', productGridElement);
        removeInputButtons.map((button) => {
            on('click', function (evt) {
                let productGridElement = closest('.js-product-grid', button);
                let nameString = button.getAttribute('data-name');

                if (!nameString) {
                    console.error('Can\'t remove product grid inputs. data-name is not set on', this);
                    return;
                }

                evt.preventDefault();

                if (nameString === '*') {
                    removeInputs(productGridElement, getInputState(productGridElement).filter(function (currentInput) {
                        return currentInput.hidden !== true;
                    }));
                } else {
                    removeInputs(productGridElement, nameString.replace(/\s/g, '').split(',').map(function (name) {
                        return {
                            'name': name
                        };
                    }));
                }
            }, button);
        });
    });
}

export function reloadGrid(productGridElement) {
    let url = getBaseUrl(productGridElement);
    let ajaxUrl = productGridElement.getAttribute('data-product-grid-ajax-url') || url;

    let params = getParams(productGridElement);
    let visibleParams = params.filter(function (input) {
        return input.hideInUrl !== true;
    });

    loadUrl(productGridElement, ajaxUrl, params);

    history.pushState({
            'productGridAjaxUrl': ajaxUrl,
            'productGridInputState': params,
            'productGridAdditionalInputState': getAdditionalInputState(productGridElement)
        },
        document.title,
        addParamsToUrl(url, visibleParams)
    );

    triggerWith('product-grid.changed', [getInputState(productGridElement)], productGridElement);
}

let pendingRequest,
    cache = [];

function loadUrl(productGridElement, ajaxUrl, params = [], loadContentOnly = false, insertMode = '') {
    params = [].concat(params);

    if (pendingRequest) {
        pendingRequest.abort();
        pendingRequest = null;
    }

    if (insertMode !== 'append') {
        if (window.pageYOffset > productGridElement.getBoundingClientRect().top) {
            window.scrollTo({
                top: productGridElement.getBoundingClientRect().top - 80,
                behavior: 'smooth'
            });
        }
    }

    if (loadContentOnly) {
        params.push({
            name: 'contentOnly',
            value: 1
        });
    }

    params.push({
        name: '_original_href',
        value: getBasePath(productGridElement)
    });

    let cacheResult = cache.filter(function(cacheEntry) {
        return cacheEntry.url === addParamsToUrl(ajaxUrl, params)
    });

    if (cacheResult.length) {
        setResults(cacheResult[0].content, productGridElement, loadContentOnly);

        if (cacheResult[0].meta.title) {
            document.title = cacheResult[0].meta.title;
        }

        let deferred = Promise.resolve(null, [cacheResult[0].content]);
        return deferred;
    } else {
        addClass('is-loading', productGridElement);
        let loadingElement = findIn('.js-product-grid__loading', productGridElement);
        removeAttribute('hidden', loadingElement);

        pendingRequest = fetch(addParamsToUrl(ajaxUrl, params), {
            method: 'GET'
        });

        pendingRequest.then((response) => {
            return response.clone().json()
        }).then((response) => {
            if (response.success) {
                setResults(response.html, productGridElement, loadContentOnly, insertMode);

                cache.push({
                    url: addParamsToUrl(ajaxUrl, params),
                    content: response.html,
                    meta: {
                        title: response.meta.title ? response.meta.title : null
                    }
                });

                if (cache.length > 40) {
                    cache.shift();
                }

                if (response.meta.title) {
                    document.title = response.meta.title;
                }

                trigger('product-grid.success', productGridElement);
                // productGridElement.trigger('product-grid.success');
            }

            pendingRequest = null;
            removeClass('is-loading', productGridElement);
            setAttribute('hidden', true, loadingElement);

        }).catch((error, requestState) => {
            if ((!error || error.name !== 'AbortError') // native fetch abort
                && requestState !== 'abort') { // jquery abort
                console.error(error);
                removeAttribute('hidden', findIn('.js-product-grid__error', productGridElement));

                pendingRequest = null;
                removeClass('is-loading', productGridElement);

                setAttribute('hidden', true, loadingElement);

                trigger('product-grid.failed', productGridElement);
                // productGridElement.trigger('product-grid.failed');
            }
        });

        return pendingRequest;
    }
}

function setResults(data, productGridElement, loadContentOnly, insertMode = '') {
    if (insertMode) {
        let containerElement = findIn('.js-product-grid__content', productGridElement);
        let responseContent = data;
        let elsToHide;

        cleanUpInScope(containerElement);

        if (insertMode === 'prepend') {
            // hide the load-previous-button and sorting-form
            elsToHide = findAllIn('.js-product-grid__hide-when-load-previous', productGridElement);
            elsToHide.map((element) => {
                setAttribute('hidden', true, element);
            });

            containerElement.innerHTML = responseContent + containerElement.innerHTML;
        } else if (insertMode === 'append') {
            // hide the load-next-button
            elsToHide = findAllIn('.js-product-grid__hide-when-load-next', productGridElement);
            elsToHide.map((element) => {
                setAttribute('hidden', true, element);
            });

            containerElement.innerHTML += responseContent;
        }


        initInScope(containerElement);
    } else {
        let containerElement = loadContentOnly
            ? findIn('.js-product-grid__content', productGridElement)
            : findIn('.js-product-grid__container', productGridElement);

        cleanUpInScope(containerElement);
        containerElement.innerHTML = data;
        initInScope(containerElement);
    }
}

// todo add support to add functionality to this function
function serializeInputElements(inputs) {
    let inputState = [];

    inputs.map((inputElement) => {
        let currentInputState = serializeInput(inputElement);

        if (currentInputState && currentInputState.value !== '') {
            // todo rename
            if (hasClass('js-product-grid__input--hidden', inputElement)) {
                currentInputState.hidden = true;
            }

            if (hasClass('js-product-grid__input--hidden-url', inputElement)) {
                currentInputState.hidden = true;
                currentInputState.hideInUrl = true;
            }

            currentInputState.text = escapeHtml(getText(inputElement));

            currentInputState = {
                ...getPrefixedDataSet('product-grid-input', inputElement),
                ...currentInputState
            };

            inputState.push(currentInputState);
        }
    });

    return inputState
}

export function getInputState(productGridElement) {
    let inputState = serializeInputElements(getChangeInputElements(productGridElement));

    findAllIn('.js-product-grid__submit-section', productGridElement).map((section) => {
        if (section.getAttribute('data-product-grid-input-state')) {
            inputState = inputState.concat(JSON.parse(section.getAttribute('data-product-grid-input-state')));
        }
    });

    inputState = inputState.concat(getAdditionalInputState(productGridElement));

    return inputState;
}

function getEmptyParams(productGridElement) {
    return getEmptyAbleInputNames(productGridElement).filter(function (name) {
        return !(getInputState(productGridElement).filter(function (currentInput) {
            return currentInput.name === name;
        })[0]);
    }).map(function (name) {
        return {
            'name': name,
            'value': '$$EMPTY$$',
            'hidden': true
        }
    });
}

function getParams(productGridElement) {
    return getInputState(productGridElement).concat(getEmptyParams(productGridElement));
}

function setInputState(productGridElement, inputState) {
    let allInputs = getInputElements(productGridElement);

    // let test = allInputs.filter(function (input) {
    //     return input.type !== 'radio' && input.type !== 'checkbox';
    // });

    allInputs.filter(function (input) {
        return input.type !== 'radio' && input.type !== 'checkbox';
    }).forEach((item) => {
        let currentInput = item;
        let result = findInInputState(inputState, currentInput.getAttribute('name'));
        let prevValue = currentInput.value;

        if (result && result.value !== '$$EMPTY$$') {
            currentInput.value = result.value;
        } else {
            currentInput.value = '';
        }

        if (prevValue !== currentInput.value) {
            trigger('changed', currentInput);
            trigger('product-grid.input-changed', currentInput);
            // currentInput.trigger('changed').trigger('product-grid.input-changed');
        }
    });

    allInputs.filter(function (input) {
        return input.type === 'radio' || input.type === 'checkbox';
    }).forEach((item) => {
        let currentInput = item;
        let result = findInInputState(inputState, currentInput.getAttribute('name'), currentInput.getAttribute('value'));
        let wasChecked = currentInput.checked;
        let shouldBeChecked = !!result && result.value !== '$$EMPTY$$';

        currentInput.checked = shouldBeChecked;

        if (shouldBeChecked !== wasChecked) {
            trigger('changed', currentInput);
        }
    });

    if (findAllIn('.js-product-grid__submit-section', productGridElement)) {
        findAllIn('.js-product-grid__submit-section', productGridElement).map((section) => {
            updateSubmitSectionDataValues(section);
        });
    }

    triggerWith('product-grid.changed', [getInputState(productGridElement)], productGridElement);
}

function findInInputState(inputState, name, value) {
    return inputState.filter(function (currentInputState) {
        if (typeof value !== 'undefined') {
            return currentInputState.name === name && currentInputState.value === value;
        } else {
            return currentInputState.name === name;
        }
    })[0];
}

function addParamsToUrl(url, params) {
    let urlWithParams = url;
    if (params && params.length) {
        urlWithParams += urlWithParams.indexOf('?') < 0 ? '?' : '&';
        urlWithParams += params.map(function(param) {
            return param.name + '=' + encodeURIComponent(param.value)
        }).join('&');
    }

    return urlWithParams;
}

function getText(inputElement) {
    let text = inputElement.getAttribute('data-text') || inputElement.value;

    if (inputElement.tagName === 'SELECT') {
        let selectedOption = inputElement[inputElement.selectedIndex];
        if (selectedOption && selectedOption.length) {
            text = selectedOption.data('text') || selectedOption.text().trim();
        }
    }

    if (inputElement.getAttribute('data-text-prefix')) {
        text = inputElement.getAttribute('data-text-prefix') + text;
    }

    if (inputElement.getAttribute('data-text-suffix')) {
        text += inputElement.getAttribute('data-text-suffix');
    }

    return text;
}

function getInputElements(scope) {
    return findAllIn(
        '.js-product-grid__input, ' +
        '.js-product-grid__form input, ' +
        '.js-product-grid__form select, ' +
        '.js-product-grid__submit-section input, ' +
        '.js-product-grid__submit-section select'
        , scope);
}

/* get all inputs that trigger a reaload on change*/
function getChangeInputElements(scope) {
    let inputs = findAllIn(
        '.js-product-grid__input, ' +
        '.js-product-grid__form input, ' +
        '.js-product-grid__form select', scope
    );
    inputs = inputs.filter((input) => {
        return !closest('.js-product-grid__submit-section ', input)
    });

    return inputs;
}

/* EmptyAble = elements that use the $$empty$$ param*/
function getEmptyAbleInputElements(scope) {
    return getInputElements(scope).filter((input) => input.getAttribute('data-product-grid-use-empty-param') || closest('[data-product-grid-use-empty-param]', input) ? input : false)
}

export function removeInput(productGridElement, name, value) {
    let newState = getInputState(productGridElement).filter(function (currentInputState) {
        if (typeof value !== 'undefined') {
            return currentInputState.name !== name || currentInputState.value !== value;
        } else {
            return currentInputState.name !== name;
        }
    });

    let additionalInputState = getAdditionalInputState(productGridElement);
    additionalInputState = additionalInputState.filter(function (currentInputState) {
        if (typeof value !== 'undefined') {
            return currentInputState.name !== name || currentInputState.value !== value;
        } else {
            return currentInputState.name !== name;
        }
    });

    productGridElement.getAttribute('data-additional-input-state', additionalInputState);

    setInputState(productGridElement, newState);
    reloadGrid(productGridElement);
}

export function removeInputs(productGridElement, inputsToRemove) {
    let newState = getInputState(productGridElement).filter(function (currentInputState) {
        let shouldBeRemoved = !!(inputsToRemove.filter(function (inputToRemove) {
            if (typeof inputToRemove.value !== 'undefined') {
                return currentInputState.name === inputToRemove.name && currentInputState.value === inputToRemove.value;
            } else {
                return currentInputState.name === inputToRemove.name;
            }
        })[0]);

        return !shouldBeRemoved;
    });

    setInputState(productGridElement, newState);
    reloadGrid(productGridElement);
}

export function setInput(productGridElement, name, value) {
    let newState = getInputState(productGridElement).map(function (currentInputState) {
        if (currentInputState.name === name) {
            return Object.assign({}, currentInputState, {
                value: value
            });
        }

        return currentInputState;
    });

    setInputState(productGridElement, newState);
    reloadGrid(productGridElement);
}

function getBaseUrl(productGridElement) {
    return location.origin + getBasePath(productGridElement);
}

function getBasePath(productGridElement) {
    if (location.search) {
        let inputNames = getAllInputNames(productGridElement);
        let additionalInputNames = getAdditionalInputState(productGridElement).map(x => x.name);
        let parmsArray = getUrlParams().filter(function (param) {
            return (
                inputNames.indexOf(param.name) < 0
                && additionalInputNames.indexOf(param.name) < 0
            ) && param.name !== 'page';
        });

        return addParamsToUrl(location.pathname, parmsArray);
    } else {
        return location.pathname;
    }
}

function getUrlParams() {
    return location.search
        ? decodeURIComponent(location.search)
            .substr(1)
            .split("&")
            .map(function (paramString) {
                let paramArr = paramString.split('=');
                return {
                    name: paramArr[0],
                    value: paramArr[1]
                }
            })
        : [];
}

function getAllInputNames(productGridElement) {
    return getNamesOfInputs(getInputElements(productGridElement));
}

/* EmptyAble = elements that use the $$empty$$ param*/
function getEmptyAbleInputNames(productGridElement) {
    return getNamesOfInputs(getEmptyAbleInputElements(productGridElement));
}

function getNamesOfInputs(inputs) {
    return inputs.reduce(function (names, input) {
        let currentName = input.getAttribute('name');
        if (currentName && names.indexOf(currentName) < 0) {
            names.push(currentName);
        }
        return names;
    }, []);
}

function updateSubmitSectionDataValues(productGridSection) {
    productGridSection.setAttribute('data-product-grid-input-state', JSON.stringify(serializeInputElements(findAllIn('input, select', productGridSection))));
}

function getAdditionalInputState(productGridElement) {
    return productGridElement.getAttribute('data-additional-input-state') || [];
}

export function setValue(productGridElement, name, value, text) {
    setValues(productGridElement, [{
        name: name,
        value: value,
        text: text
    }]);
}

export function setValues(productGridElement, values) {
    let inputState = getAdditionalInputState(productGridElement);

    values.forEach(function (currentValue) {
        let existingValue = (inputState.filter(function (x) {
            return currentValue.name === x.name;
        })[0]);

        if (existingValue) {
            existingValue.value = currentValue.value;
        } else {
            inputState.push(currentValue);
        }
    });

    productGridElement.getAttribute('data-additional-input-state', inputState);
    reloadGrid(productGridElement);
}

function serializeInput(input) {
    let inputData;

    if (input.name && !input.disabled && input.type != 'file' && input.type != 'reset' && input.type != 'submit' && input.type != 'button') {
        if (input.type == 'select-multiple') {
            for (let i = 0; i < input.options.length; i++) {
                if (input.options[i].selected)
                    inputData = {name: input.name, value: input.options[i].value};
            }
        } else if ((input.type != 'checkbox' && input.type != 'radio') || input.checked) {
            inputData = { name: input.name, value: input.value };
        }
    }

    return inputData;
}